import { RequestOptions } from '@algolia/transporter';
import { ICONS_SEARCH } from 'constants/mixpanel';
import { Icons8IllustrationSearchResponse, Icons8SearchResponse } from 'libs/e1api/icons8';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useState } from 'react';
import useSWRInfinite from 'swr/infinite';
import { IconColorOption } from 'types/icon.types';

import { hasMoreIcons8IllustrationIcons } from '../common/usePaginatedIcon.helpers';
import { IconSearchData } from '../common/usePaginatedIcon.types';
import { emptyIcons8IllustrationQuery, emptyIcons8Query, emptyQuery } from './usePaginatedIconSearch.config';
import {
  ICONS8_ILLUSTRATION_HITS_PER_PAGE,
  fetchAlgoliaIcons,
  fetchIcons8Icons,
  fetchIcons8Illustrations,
  generateKey,
  hasAllIconOption,
  hasMoreIcons8Icons,
  parseAllIcons,
} from './usePaginatedIconSearch.helpers';
import { PaginatedIconSearch, PaginatedIconSearchOptions, SearchApiResponse } from './usePaginatedIconSearch.types';

/**
 *  Hook that returns Algolia + Icons8 icon search functionality.
 *
 *  Comes with retries and caching and pagination.
 *  The hook returns:
 *      - Functions for searching icons
 *      - The search state (e.g. loading, error)
 *      - The icon search result
 * @returns {PaginatedIconSearch}
 */
export const usePaginatedIconSearch = (options?: PaginatedIconSearchOptions): PaginatedIconSearch => {
  const { colorOptions = [IconColorOption.All, IconColorOption.Color, IconColorOption.Mono] } = options || {};

  const [query, setQuery] = useState<string>('');
  const [selectedColorOption, setSelectedColorOption] = useState<IconColorOption>(
    // if "all" icon styles are included in the color options it should be selected as the style
    hasAllIconOption(colorOptions) ? IconColorOption.All : colorOptions[0],
  );

  // cache key for icon search query result per page and selectedColorOption
  const getAlgoliaKey = (page: number) => generateKey('ALGOLIA', query, page, selectedColorOption);
  const getIcons8Key = (page: number) => generateKey('ICONS8', query, page, selectedColorOption);
  const getIcons8IllustrationKey = (page: number) =>
    generateKey('ICONS8ILLUSTRATION', query, page, selectedColorOption);

  const swrConfig = {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateFirstPage: false,
    // skip fetch if there is no query
    fallbackData: query ? undefined : [emptyQuery],
  };

  const {
    data: initialAlgoliaData,
    error: algoliaError,
    size: algoliaPage,
    setSize: setAlgoliaPage,
  } = useSWRInfinite<RequestOptions>(getAlgoliaKey, fetchAlgoliaIcons, swrConfig);

  const {
    data: initialIcons8Data,
    error: icons8Error,
    size: icons8Page,
    setSize: setIcons8Page,
  } = useSWRInfinite<RequestOptions>(getIcons8Key, fetchIcons8Icons, {
    ...swrConfig,
    fallbackData: query ? undefined : [emptyIcons8Query],
  });

  const {
    data: initialIcons8IllustrationsData,
    error: icons8IllustrationsError,
    size: icons8IllustrationsPage,
    setSize: setIcons8IllustrationsPage,
  } = useSWRInfinite<RequestOptions>(getIcons8IllustrationKey, fetchIcons8Illustrations, {
    ...swrConfig,
    fallbackData: query ? undefined : [emptyIcons8IllustrationQuery],
  });

  const resetPage = () => {
    setAlgoliaPage(1);
    setIcons8Page(1);
    setIcons8IllustrationsPage(1);
  };

  const algoliaData = initialAlgoliaData as SearchApiResponse[];
  const icons8Data = initialIcons8Data as Icons8SearchResponse[];
  const icons8IllustrationsData = initialIcons8IllustrationsData as Icons8IllustrationSearchResponse[];

  const searchIcons = (search: string) => {
    Mixpanel.track(ICONS_SEARCH.eventName, {
      query: search,
      filter: selectedColorOption,
    });

    if (hasAllIconOption(colorOptions)) setSelectedColorOption(IconColorOption.All);
    setQuery(search);
  };

  const clearSearch = () => {
    if (!query) return;

    if (hasAllIconOption(colorOptions)) setSelectedColorOption(IconColorOption.All);
    setQuery('');
    resetPage();
  };

  const hasMoreAlgoliaIcons =
    algoliaData && algoliaData.length
      ? algoliaData[algoliaData.length - 1]?.nbPages - 1 > algoliaData[algoliaData.length - 1]?.page
      : false;
  const hasMoreIcons8 = !!icons8Data?.length && hasMoreIcons8Icons(icons8Data?.[icons8Data.length - 1]);
  const hasMoreIcons8Illustrations =
    !!icons8IllustrationsData?.length &&
    hasMoreIcons8IllustrationIcons(
      icons8IllustrationsData?.[icons8IllustrationsData.length - 1],
      icons8IllustrationsPage,
      ICONS8_ILLUSTRATION_HITS_PER_PAGE,
    );

  /**
   * Load icons from Algolia and Icons8 if they have more pages
   */
  const onShowMore = () => {
    // If there is more algolia pages, increment the page
    if (hasMoreAlgoliaIcons) {
      setAlgoliaPage(algoliaPage + 1);
    }

    // If there are more icons8 pages, increment the page
    if (hasMoreIcons8) {
      setIcons8Page(icons8Page + 1);
    }

    if (hasMoreIcons8Illustrations) {
      setIcons8IllustrationsPage(icons8IllustrationsPage + 1);
    }
  };

  const icons: IconSearchData[] = parseAllIcons(algoliaData, icons8Data, icons8IllustrationsData);

  const hasMore = hasMoreAlgoliaIcons || hasMoreIcons8 || hasMoreIcons8Illustrations;

  const isLoadingInitialAlgoliaData = !algoliaData && !algoliaError;
  const isLoadingInitialIcons8Data = !icons8Data && !icons8Error;
  const isLoadingInitialIcons8IllustrationsData = !icons8IllustrationsData && !icons8IllustrationsError;

  const isLoadingInitialData =
    isLoadingInitialAlgoliaData || isLoadingInitialIcons8Data || isLoadingInitialIcons8IllustrationsData;

  const isLoadingMoreAlgoliaData =
    algoliaPage > 0 && algoliaData && typeof algoliaData[algoliaPage - 1] === 'undefined';
  const isLoadingMoreIcons8Data = icons8Page > 0 && icons8Data && typeof icons8Data[icons8Page - 1] === 'undefined';
  const isLoadingMoreIcons8IllustrationsData =
    icons8IllustrationsPage > 0 &&
    icons8IllustrationsData &&
    typeof icons8IllustrationsData[icons8IllustrationsPage - 1] === 'undefined';

  const isLoadingMore = !!(
    isLoadingInitialData ||
    isLoadingMoreAlgoliaData ||
    isLoadingMoreIcons8Data ||
    isLoadingMoreIcons8IllustrationsData
  );

  const hasNoResultsFound = !!(icons.length === 0 && !isLoadingInitialData && query);

  // if the icon style is not included in the color options, set the icon style to the first color option
  if (!colorOptions.includes(selectedColorOption) && colorOptions.length) setSelectedColorOption(colorOptions[0]);

  return {
    // Only consider error if both search engines have an error
    error: algoliaError && icons8Error,
    hasMore,
    isLoadingMore,
    isLoadingInitialData,
    colorOptions,
    selectedColorOption,
    setSelectedColorOption,
    query,
    icons: !isLoadingInitialData ? icons : [],
    onShowMore,
    clearSearch,
    searchIcons,
    hasNoResultsFound,
  };
};
